import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { 
  Check, 
  X, 
  Minus, 
  Plus, 
  Loader2, 
  ChevronDown,
  ChevronUp,
  Zap,
  Users,
  Building2,
  ExternalLink,
  CreditCard,
  Lock,
  ArrowUp
} from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { BuyCreditsModal } from '../components/BuyCreditsModal';
import { CreditPackCards } from '../components/CreditPackCards';

const FEATURES = [
  { key: 'audio_recording', label: 'Gravação de áudio' },
  { key: 'transcription', label: 'Transcrição automática' },
  { key: 'ai_documentation', label: 'Documentação com IA' },
  { key: 'basic_patient_record', label: 'Prontuário básico' },
  { key: 'pdf_export', label: 'Exportação PDF' },
  { key: 'clinic_logo', label: 'Logo da clínica nos documentos' },
  { key: 'custom_prompts', label: 'Prompts de IA personalizados' },
  { key: 'full_clinical_history', label: 'Histórico clínico completo' },
  { key: 'email_support', label: 'Suporte por email' },
  { key: 'admin_dashboard', label: 'Dashboard administrativo' },
  { key: 'member_management', label: 'Gerenciamento de membros' },
  { key: 'consolidated_reports', label: 'Relatórios consolidados' },
  { key: 'priority_support', label: 'Suporte prioritário (24h)' },
  { key: 'dedicated_onboarding', label: 'Onboarding dedicado' }
];

const FAQ_ITEMS = [
  {
    question: 'Posso cancelar a qualquer momento?',
    answer: 'Sim! Você pode cancelar sua assinatura a qualquer momento. O acesso continua até o final do período já pago.'
  },
  {
    question: 'O que acontece quando atinjo o limite de consultas?',
    answer: 'No plano Starter, você precisará fazer upgrade para continuar. Nos planos pagos (Solo e Clínica), consultas adicionais são cobradas a R$0,90 cada no final do ciclo.'
  },
  {
    question: 'Como funciona a cobrança por assento?',
    answer: 'No plano Clínica, cada assento representa um fisioterapeuta da equipe. Você paga R$99/mês por assento (ou R$79,20 no plano anual). Assentos podem ser adicionados a qualquer momento, com cobrança proporcional.'
  },
  {
    question: 'Existe desconto para pagamento anual?',
    answer: 'Sim! O pagamento anual oferece 20% de desconto em todos os planos. No Solo, você paga R$103,20/mês em vez de R$129. No Clínica, R$79,20/assento/mês em vez de R$99.'
  },
  {
    question: 'Tem plano para redes com mais de 20 unidades?',
    answer: 'Sim! Para redes com mais de 20 fisioterapeutas, oferecemos condições especiais. Entre em contato conosco para um plano Enterprise personalizado.'
  }
];

export default function BillingPage() {
  const [isAnnual, setIsAnnual] = useState(false);
  const [clinicSeats, setClinicSeats] = useState(3);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [creditLoadingPack, setCreditLoadingPack] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [txError, setTxError] = useState(false);
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    loadSubscription();
    loadUsageStats();
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    setTxError(false);
    setTransactions(null);
    try {
      const data = await api.getBillingTransactions();
      setTransactions(data.transactions || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTxError(true);
      setTransactions([]);
    }
  };

  const loadSubscription = async () => {
    try {
      const data = await api.getSubscription();
      setCurrentPlan(data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadUsageStats = async () => {
    try {
      const data = await api.getUsageStats();
      setUsageStats(data);
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await api.openCustomerPortal();
      window.location.href = response.portal_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao abrir portal de assinatura');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCheckout = async (planType) => {
    if (planType === 'starter') {
      toast.info('Você já está no plano Starter gratuito!');
      return;
    }

    setLoading(true);
    try {
      const response = await api.createCheckout({
        plan_type: planType,
        billing_interval: isAnnual ? 'annual' : 'monthly',
        seats: planType === 'clinica' ? clinicSeats : 1,
        origin_url: window.location.origin
      });

      window.location.href = response.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar checkout');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (planType) => {
    if (planType === 'starter') return { display: 'Grátis', total: 0 };
    
    if (planType === 'solo') {
      if (isAnnual) {
        return { 
          display: 'R$ 103,20', 
          period: '/mês',
          total: 1238.40,
          originalPrice: 129,
          savings: 309.60
        };
      }
      return { display: 'R$ 129', period: '/mês', total: 129 };
    }
    
    if (planType === 'clinica') {
      const pricePerSeat = isAnnual ? 79.20 : 99;
      const total = pricePerSeat * clinicSeats * (isAnnual ? 12 : 1);
      return {
        display: `R$ ${pricePerSeat.toFixed(2).replace('.', ',')}`,
        period: '/assento/mês',
        total: isAnnual ? total : pricePerSeat * clinicSeats,
        monthlyTotal: pricePerSeat * clinicSeats,
        originalPrice: isAnnual ? 99 : null,
        savings: isAnnual ? (99 - 79.20) * clinicSeats * 12 : 0
      };
    }
    
    return { display: '—', total: 0 };
  };

  const isCurrentPlan = (planType) => {
    return currentPlan?.plan_type === planType;
  };

  const handleBuyCreditsPack = async (pack) => {
    setCreditLoadingPack(pack);
    try {
      const result = await api.buyCredits(pack);
      window.location.href = result.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao iniciar checkout.');
      setCreditLoadingPack(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto py-8" data-testid="billing-page">
        {/* Current Subscription Info */}
        {currentPlan && currentPlan.plan_type !== 'starter' && (
          <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-2xl border p-6 mb-8`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
                  Sua Assinatura
                </h2>
                <div className="flex items-center gap-4">
                  <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Plano <span className="font-semibold">{currentPlan.plan_name}</span>
                  </span>
                  {currentPlan.subscription_status === 'active' && (
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      Ativo
                    </span>
                  )}
                  {currentPlan.subscription_status === 'past_due' && (
                    <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      Pagamento pendente
                    </span>
                  )}
                </div>
              </div>
              
              {/* Usage Stats */}
              {usageStats && (
                <div className={`${isDark ? 'bg-slate-900/50' : 'bg-slate-50'} rounded-lg p-4 min-w-[200px]`}>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'} mb-1`}>
                    Uso este mês
                  </p>
                  <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {usageStats.consultations_used} / {usageStats.consultations_included}
                  </p>
                  <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                    <div 
                      className={`h-2 rounded-full ${usageStats.percentage_used >= 90 ? 'bg-red-500' : usageStats.percentage_used >= 70 ? 'bg-amber-500' : 'bg-primary'}`}
                      style={{ width: `${Math.min(100, usageStats.percentage_used)}%` }}
                    ></div>
                  </div>
                  {usageStats.overage_consultations > 0 && (
                    <p className={`text-xs mt-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                      +{usageStats.overage_consultations} excedentes (R$ {usageStats.overage_amount.toFixed(2).replace('.', ',')})
                    </p>
                  )}
                </div>
              )}
              
              <Button
                variant="outline"
                onClick={handleOpenPortal}
                disabled={portalLoading}
                className={`${isDark ? 'border-slate-600 hover:bg-slate-700' : ''}`}
                data-testid="manage-subscription-btn"
              >
                {portalLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Gerenciar assinatura
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
              {usageStats?.can_buy_credits && (
                <Button
                  variant="outline"
                  onClick={() => setCreditsModalOpen(true)}
                  className={`${isDark ? 'border-slate-600 hover:bg-slate-700' : ''}`}
                  data-testid="billing-buy-credits-btn"
                >
                  Comprar créditos
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>
            Planos e Preços
          </h1>
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'} max-w-2xl mx-auto mb-8`}>
            Escolha o plano ideal para sua prática. Comece grátis e evolua conforme sua necessidade.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${!isAnnual ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-slate-400' : 'text-slate-500')}`}>
              Mensal
            </span>
            <Switch
              checked={isAnnual}
              onCheckedChange={setIsAnnual}
              data-testid="billing-toggle"
            />
            <span className={`text-sm font-medium ${isAnnual ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-slate-400' : 'text-slate-500')}`}>
              Anual
            </span>
            {isAnnual && (
              <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-1 rounded-full">
                -20%
              </span>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div id="plans-grid" className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {/* Starter Plan */}
          <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-2xl border p-8 relative`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-xl flex items-center justify-center">
                <Zap className={`h-6 w-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} />
              </div>
              <div>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Starter</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Para começar</p>
              </div>
            </div>

            <div className="mb-6">
              <div className={`text-4xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {getPrice('starter').display}
              </div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} mt-1`}>
                15 consultas/mês
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                Gravação de áudio
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                Transcrição automática
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                Documentação com IA
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <X className="h-5 w-5 flex-shrink-0" />
                Exportação PDF
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <X className="h-5 w-5 flex-shrink-0" />
                Logo nos documentos
              </li>
            </ul>

            <Button
              className={`w-full ${isCurrentPlan('starter') ? 'bg-slate-200 text-slate-700 cursor-default' : isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-900'}`}
              disabled={isCurrentPlan('starter')}
              onClick={() => navigate('/register')}
              data-testid="starter-cta"
            >
              {isCurrentPlan('starter') ? 'Plano atual' : 'Começar gratuitamente'}
            </Button>
            <p className={`text-xs text-center mt-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Sem cartão de crédito
            </p>
          </div>

          {/* Solo Plan */}
          <div className={`${isDark ? 'bg-slate-800/50' : 'bg-white'} rounded-2xl border-2 border-primary p-8 relative shadow-lg shadow-primary/10`}>
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-sm font-semibold px-4 py-1 rounded-full">
              Mais escolhido
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Solo</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Profissional autônomo</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className={`text-4xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {getPrice('solo').display}
                </span>
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {getPrice('solo').period}
                </span>
              </div>
              {isAnnual && (
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} mt-1`}>
                  <span className="line-through">R$ 129</span> • Economia de R$ {getPrice('solo').savings?.toFixed(2).replace('.', ',')}
                </p>
              )}
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} mt-1`}>
                80 consultas/mês + R$ 0,90/extra
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                Tudo do Starter
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                Exportação PDF com logo
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                Prompts personalizados
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                Histórico completo
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                Suporte por email (48h)
              </li>
            </ul>

            <Button
              className={`w-full ${isCurrentPlan('solo') ? 'bg-primary/50 cursor-default' : 'bg-primary hover:bg-primary-hover'} text-white`}
              disabled={loading || isCurrentPlan('solo')}
              onClick={() => handleCheckout('solo')}
              data-testid="solo-cta"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isCurrentPlan('solo') ? (
                'Plano atual'
              ) : (
                'Assinar Solo'
              )}
            </Button>
          </div>

          {/* Clínica Plan */}
          <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-2xl border p-8 relative`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                <Building2 className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Clínica</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Para equipes</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-2">
                <span className={`text-4xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {getPrice('clinica').display}
                </span>
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {getPrice('clinica').period}
                </span>
              </div>
              {isAnnual && (
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} mt-1`}>
                  <span className="line-through">R$ 99</span>/assento/mês
                </p>
              )}
            </div>

            {/* Seats Selector */}
            <div className={`${isDark ? 'bg-slate-900/50' : 'bg-slate-50'} rounded-lg p-4 mb-4`}>
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'} mb-2 block`}>
                Quantos fisioterapeutas?
              </label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setClinicSeats(Math.max(3, clinicSeats - 1))}
                  disabled={clinicSeats <= 3}
                  className={isDark ? 'border-slate-600' : ''}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  min={3}
                  max={20}
                  value={clinicSeats}
                  onChange={(e) => setClinicSeats(Math.min(20, Math.max(3, parseInt(e.target.value) || 3)))}
                  className={`w-20 text-center ${isDark ? 'bg-slate-800 border-slate-600 text-white' : ''}`}
                  data-testid="seats-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setClinicSeats(Math.min(20, clinicSeats + 1))}
                  disabled={clinicSeats >= 20}
                  className={isDark ? 'border-slate-600' : ''}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mt-3`}>
                Total: R$ {getPrice('clinica').monthlyTotal?.toFixed(2).replace('.', ',')}/mês
              </p>
              {clinicSeats >= 20 && (
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'} mt-1`}>
                  Para mais de 20 assentos, <a href="mailto:contato@akali.com.br" className="text-primary hover:underline">entre em contato</a>
                </p>
              )}
            </div>

            <ul className="space-y-3 mb-8">
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-secondary flex-shrink-0" />
                Tudo do Solo × assentos
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-secondary flex-shrink-0" />
                Dashboard administrativo
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-secondary flex-shrink-0" />
                Relatórios consolidados
              </li>
              <li className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <Check className="h-5 w-5 text-secondary flex-shrink-0" />
                Suporte prioritário (24h)
              </li>
            </ul>

            <Button
              className={`w-full ${isCurrentPlan('clinica') ? 'bg-secondary/50 cursor-default' : 'bg-secondary hover:bg-secondary-hover'} text-white`}
              disabled={loading || isCurrentPlan('clinica')}
              onClick={() => handleCheckout('clinica')}
              data-testid="clinica-cta"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isCurrentPlan('clinica') ? (
                'Plano atual'
              ) : (
                'Assinar Clínica'
              )}
            </Button>
          </div>
        </div>

        {/* Credit Packs Section */}
        {usageStats && (
          <div className="mb-16" data-testid="credit-packs-section">
            <div className="text-center mb-8">
              <h2 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
                Créditos avulsos
              </h2>
              <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} max-w-2xl mx-auto text-sm`}>
                Consultas adicionais em pagamento único. Os créditos não expiram e são usados após o limite do seu plano.
              </p>
            </div>
            {usageStats.can_buy_credits ? (
              <div className="max-w-4xl mx-auto">
                <CreditPackCards onBuy={handleBuyCreditsPack} loadingPack={creditLoadingPack} />
                <p className={`text-xs text-center mt-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Pagamento processado de forma segura pelo Stripe.
                </p>
              </div>
            ) : (
              <div
                className={`max-w-2xl mx-auto rounded-2xl border p-8 text-center ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                data-testid="credit-packs-locked"
              >
                <Lock className={`h-8 w-8 mx-auto mb-3 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
                  Disponível nos planos Solo e Clínica
                </h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} mb-5`}>
                  Créditos avulsos a partir de R$ 0,99 por consulta. Assine um plano pago para desbloquear.
                </p>
                <Button
                  onClick={() => document.getElementById('plans-grid')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid="credit-packs-upgrade-cta"
                >
                  <ArrowUp className="h-4 w-4 mr-2" />
                  Ver planos Solo e Clínica
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Feature Comparison Table */}
        <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-2xl border overflow-hidden mb-16`}>
          <div className={`px-6 py-5 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Comparativo de Funcionalidades
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'bg-slate-900/50' : 'bg-slate-50'}>
                  <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Funcionalidade
                  </th>
                  <th className={`px-6 py-4 text-center text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Starter
                  </th>
                  <th className={`px-6 py-4 text-center text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Solo
                  </th>
                  <th className={`px-6 py-4 text-center text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Clínica
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-100'}`}>
                <tr>
                  <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Consultas/mês
                  </td>
                  <td className={`px-6 py-4 text-center text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    15
                  </td>
                  <td className={`px-6 py-4 text-center text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    80
                  </td>
                  <td className={`px-6 py-4 text-center text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    80/assento
                  </td>
                </tr>
                {FEATURES.map((feature) => (
                  <tr key={feature.key}>
                    <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      {feature.label}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {['audio_recording', 'transcription', 'ai_documentation', 'basic_patient_record'].includes(feature.key) ? (
                        <Check className="h-5 w-5 text-primary mx-auto" />
                      ) : (
                        <X className={`h-5 w-5 mx-auto ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {['admin_dashboard', 'member_management', 'consolidated_reports', 'priority_support', 'dedicated_onboarding'].includes(feature.key) ? (
                        <X className={`h-5 w-5 mx-auto ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                      ) : (
                        <Check className="h-5 w-5 text-primary mx-auto" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="h-5 w-5 text-secondary mx-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} text-center mb-8`}>
            Perguntas Frequentes
          </h2>
          <div className="space-y-4">
            {FAQ_ITEMS.map((item, index) => (
              <div
                key={`faq-${item.question.slice(0, 50)}`}
                className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border overflow-hidden`}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className={`w-full px-6 py-4 flex items-center justify-between text-left ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'} transition-colors`}
                  data-testid={`faq-${index}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {item.question}
                  </span>
                  {expandedFaq === index ? (
                    <ChevronUp className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                  ) : (
                    <ChevronDown className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className={`px-6 pb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Invoice History Section */}
        {currentPlan && currentPlan.plan_type !== 'starter' && (
          <div className={`max-w-4xl mx-auto mt-16 ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-2xl border overflow-hidden`}>
            <div className={`px-6 py-5 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Histórico de Faturas
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="billing-history-table">
                <thead>
                  <tr className={isDark ? 'bg-slate-900/50' : 'bg-slate-50'}>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Data
                    </th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Descrição
                    </th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Valor
                    </th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-100'}`}>
                  {transactions === null ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center">
                        <Loader2 className={`h-5 w-5 animate-spin mx-auto ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                      </td>
                    </tr>
                  ) : txError ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center" data-testid="billing-history-error">
                        <p className={`text-sm mb-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          Não foi possível carregar o histórico de pagamentos.
                        </p>
                        <Button variant="outline" size="sm" onClick={loadTransactions} data-testid="billing-history-retry">
                          Tentar novamente
                        </Button>
                      </td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className={`px-6 py-8 text-center text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                        data-testid="billing-history-empty"
                      >
                        Nenhum pagamento registrado ainda.
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx) => (
                      <tr key={tx.id} data-testid="billing-history-row">
                        <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                          {new Date(tx.date).toLocaleDateString('pt-BR')}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                          {tx.description}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                          R$ {Number(tx.amount).toFixed(2).replace('.', ',')}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                            Pago
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className={`px-6 py-4 border-t text-xs ${isDark ? 'border-slate-700 text-slate-500' : 'border-slate-200 text-slate-400'}`}>
              Recibos e notas fiscais oficiais estão disponíveis em "Gerenciar assinatura" (portal seguro do Stripe).
            </div>
          </div>
        )}

        {/* Cancel Subscription Link */}
        {currentPlan && currentPlan.plan_type !== 'starter' && (
          <div className="max-w-4xl mx-auto mt-8 text-center">
            <Dialog>
              <DialogTrigger asChild>
                <button className={`text-sm ${isDark ? 'text-slate-500 hover:text-slate-400' : 'text-slate-400 hover:text-slate-600'} transition-colors`}>
                  Cancelar assinatura
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cancelar assinatura?</DialogTitle>
                  <DialogDescription>
                    Tem certeza que deseja cancelar sua assinatura? Você perderá acesso ao Akali AI ao final do período já pago.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <DialogTrigger asChild>
                    <Button variant="default">
                      Manter assinatura
                    </Button>
                  </DialogTrigger>
                  <Button 
                    variant="outline" 
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={handleOpenPortal}
                  >
                    Confirmar cancelamento
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
      <BuyCreditsModal
        open={creditsModalOpen}
        onOpenChange={setCreditsModalOpen}
        canBuyCredits={usageStats?.can_buy_credits !== false}
      />
    </DashboardLayout>
  );
}
